import { flowRight } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { overrideSettingsInOneApp } from '../../../app/actions/override-settings-in-one-app';
import {
  VIEW_MODE_EDITOR,
  getRouterDataSearchParam,
  getViewModeSearchParam,
  isOneAppSearchParam,
  fetchTPASettings,
} from '@wix/communities-blog-client-common';
import { PAGE_BORDER_WIDTH_PATH } from '../../constants/wix-params';
import { setSearchParams } from '../../../app/actions/set-search-params';
import { setDemoMode } from '../../../app/actions/set-demo-mode';
import { setExperiments } from '../../../app/actions/set-experiments';
import { setUser } from '../../../app/actions/set-user';
import { setViewMode } from '../../../app/actions/set-view-mode';
import AccessibilityListener from '../../../app/components/accessibility-listener/index';
import MessageRoot from '../../../app/containers/message-root/index';
import DemoMessageRoot from '../../../app/containers/demo-message-root/index';
import Wix from '../../../app/services/wix-sdk-polyfill';
import ModalLoader from '../../../app/containers/modal-loader';

import { getViewMode as getViewModeFromState } from '../../../app/selectors/view-mode-selectors';
import withAppSettings from '../../../app/hoc/with-app-settings';
import withAuth from '../../../app/hoc/with-auth';
import withDeviceType from '../../../app/hoc/with-device-type';
import styles from './widget.scss';
import { loadAndPersistWixModal } from '../../../app/actions/load-and-persist-wix-modal';
import { setAppLoaded } from '../../../app/actions/app-loaded';
import { getIsDemoMode } from '../../../app/selectors/is-demo-mode-selectors';
import { fetchAppSettings } from '../../../app/actions/fetch-app-settings';
import { setCustomRoutes } from '../../../app/store/custom-routes/custom-routes-actions';

export default function getWidget(ContentComponent) {
  class Widget extends Component {
    static fetchData = ({ store, user, demoMode, experiments, searchParams }) => {
      const promises = [
        store.dispatch(setSearchParams(searchParams)),
        store.dispatch(setViewMode(getViewModeSearchParam(store.getState()))),
        store.dispatch(setDemoMode(Boolean(demoMode))),
        store.dispatch(setExperiments(experiments)),
        store.dispatch(fetchTPASettings()),
        store.dispatch(fetchAppSettings()),
      ];
      if (user) {
        promises.push(store.dispatch(setUser(user)));
      }
      return Promise.all(promises).then(() => store.dispatch(overrideSettingsInOneApp()));
    };

    state = { siteMemberId: null };

    async componentDidMount() {
      this.setSiteMemberId();
      Wix.addEventListener('STATE_CHANGED', () => this.setSiteMemberId());

      this.props.loadAndPersistWixModal();
      await this.props.setCustomRoutes();
      this.props.setAppLoaded();
    }

    componentWillReceiveProps({ viewMode }) {
      if (this.props.viewMode !== viewMode) {
        this.setSiteMemberId(viewMode);
      }
    }

    setSiteMemberId = (viewMode = this.props.viewMode) => {
      if (this.props.isDemoMode) {
        return;
      }

      if (viewMode === VIEW_MODE_EDITOR || this.props.isOneApp) {
        return this.setState({ siteMemberId: this.props.currentUser.siteMemberId });
      }

      this.setState({ siteMemberId: this.props.routerData.viewedUser.id });
    };

    render() {
      const { siteMemberId } = this.state;
      const { borderWidth, isDesktop, isMobile, isOneApp, isDemoMode } = this.props;
      const className = {
        'app-desktop': isDesktop,
        'app-mobile': isMobile,
        'is-one-app': isOneApp,
      };
      return (
        <div id="content-wrapper">
          <div
            className={classNames(styles.widget, className, 'page-background-color', 'page-border-color')}
            style={{ borderWidth: isDesktop ? borderWidth : 0 }}
          >
            <AccessibilityListener />
            <MessageRoot />
            <DemoMessageRoot />
            <ModalLoader />
            {(siteMemberId || isDemoMode) && <ContentComponent siteMemberId={siteMemberId} />}
          </div>
        </div>
      );
    }
  }

  Widget.propTypes = {
    borderWidth: PropTypes.number,
    currentUser: PropTypes.object,
    isDesktop: PropTypes.bool,
    isMobile: PropTypes.bool,
    isDemoMode: PropTypes.bool,
    viewMode: PropTypes.string,
    isOneApp: PropTypes.bool.isRequired,
    routerData: PropTypes.object,
    loadAndPersistWixModal: PropTypes.func,
    setAppLoaded: PropTypes.func,
    setCustomRoutes: PropTypes.func,
  };

  const mapStateToProps = (state) => ({
    viewMode: getViewModeFromState(state),
    isOneApp: isOneAppSearchParam(state),
    routerData: getRouterDataSearchParam(state),
    isDemoMode: getIsDemoMode(state),
  });

  return flowRight(
    connect(mapStateToProps, { loadAndPersistWixModal, setAppLoaded, setCustomRoutes }),
    withAppSettings({ borderWidth: PAGE_BORDER_WIDTH_PATH }),
    withAuth,
    withDeviceType,
  )(Widget);
}
